import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Bank, CreditCard, Receipt } from '@phosphor-icons/react';

const PAYMENT_METHODS = [
  { value: 'ach', label: 'ACH Bank Transfer', icon: Bank },
  { value: 'credit_card', label: 'Credit Card', icon: CreditCard },
  { value: 'check', label: 'Check', icon: Receipt },
  { value: 'wire', label: 'Wire Transfer', icon: Bank },
];

export { PAYMENT_METHODS };

const OHIO_COUNTIES = [
  'Adams','Allen','Ashland','Ashtabula','Athens','Auglaize','Belmont','Brown',
  'Butler','Carroll','Champaign','Clark','Clermont','Clinton','Columbiana',
  'Coshocton','Crawford','Cuyahoga','Darke','Defiance','Delaware','Erie',
  'Fairfield','Fayette','Franklin','Fulton','Gallia','Geauga','Greene','Guernsey',
  'Hamilton','Hancock','Hardin','Harrison','Henry','Highland','Hocking','Holmes',
  'Huron','Jackson','Jefferson','Knox','Lake','Lawrence','Licking','Logan',
  'Lorain','Lucas','Madison','Mahoning','Marion','Medina','Meigs','Mercer',
  'Miami','Monroe','Montgomery','Morgan','Morrow','Muskingum','Noble','Ottawa',
  'Paulding','Perry','Pickaway','Pike','Portage','Preble','Putnam','Richland',
  'Ross','Sandusky','Scioto','Seneca','Shelby','Stark','Summit','Trumbull',
  'Tuscarawas','Union','Van Wert','Vinton','Warren','Washington','Wayne',
  'Williams','Wood','Wyandot',
];

const set = (obj, path, val) => {
  const keys = path.split('.');
  const last = keys.pop();
  let cur = { ...obj };
  let ref = cur;
  for (const k of keys) {
    ref[k] = { ...ref[k] };
    ref = ref[k];
  }
  ref[last] = val;
  return cur;
};

export const AgencyFormDialog = ({ open, onOpenChange, formData, setFormData, onSubmit, mode = 'create' }) => {
  const isCreate = mode === 'create';
  const f = (path, value) => setFormData(set(formData, path, value));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'Create New Agency' : 'Edit Agency'}</DialogTitle>
          {isCreate && <DialogDescription>Add a new client agency to the AZAI billing platform.</DialogDescription>}
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="contracts">Contracts</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
          </TabsList>

          {/* Basic Info */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Agency Name *</Label>
                <Input value={formData.name} onChange={(e) => f('name', e.target.value)} placeholder="ABC Home Care" />
              </div>
              <div>
                <Label>NPI (10 digits) *</Label>
                <Input value={formData.npi} onChange={(e) => f('npi', e.target.value)} placeholder="1234567890" maxLength={10} />
              </div>
              <div>
                <Label>Tax ID (EIN) *</Label>
                <Input value={formData.tax_id} onChange={(e) => f('tax_id', e.target.value)} placeholder="12-3456789" />
              </div>
            </div>
            <div className="space-y-4">
              <Label className="text-base font-semibold">Business Address</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Street Address *</Label><Input value={formData.address.street1} onChange={(e) => f('address.street1', e.target.value)} placeholder="123 Main Street" /></div>
                <div className="col-span-2"><Label>Street Address 2</Label><Input value={formData.address.street2 || ''} onChange={(e) => f('address.street2', e.target.value)} placeholder="Suite 100" /></div>
                <div><Label>City *</Label><Input value={formData.address.city} onChange={(e) => f('address.city', e.target.value)} placeholder="Columbus" /></div>
                <div><Label>ZIP Code *</Label><Input value={formData.address.zip_code} onChange={(e) => f('address.zip_code', e.target.value)} placeholder="43215" /></div>
                <div>
                  <Label>County</Label>
                  <Select value={formData.address.county || ''} onValueChange={(v) => f('address.county', v)}>
                    <SelectTrigger><SelectValue placeholder="Select county" /></SelectTrigger>
                    <SelectContent>{OHIO_COUNTIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Contact */}
          <TabsContent value="contact" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Contact Name *</Label><Input value={formData.contact.name} onChange={(e) => f('contact.name', e.target.value)} placeholder="John Smith" /></div>
              <div><Label>Title</Label><Input value={formData.contact.title || ''} onChange={(e) => f('contact.title', e.target.value)} placeholder="Administrator" /></div>
              <div><Label>Phone *</Label><Input value={formData.contact.phone} onChange={(e) => f('contact.phone', e.target.value)} placeholder="(614) 555-1234" /></div>
              <div><Label>Fax</Label><Input value={formData.contact.fax || ''} onChange={(e) => f('contact.fax', e.target.value)} placeholder="(614) 555-1235" /></div>
              <div className="col-span-2"><Label>Email *</Label><Input type="email" value={formData.contact.email} onChange={(e) => f('contact.email', e.target.value)} placeholder="admin@agency.com" /></div>
            </div>
          </TabsContent>

          {/* Contracts */}
          <TabsContent value="contracts" className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">Enable contracts as needed.</p>
            {[
              { key: 'medicaid', title: 'Ohio Medicaid', desc: 'ODM fee-for-service billing', field: 'provider_id', ph: '0000000' },
              { key: 'embs', title: 'Ohio eMBS', desc: 'DODD waiver billing portal', field: 'contract_number', ph: '0000000' },
              { key: 'sandata', title: 'Sandata EVV', desc: 'Electronic Visit Verification', field: 'provider_id', ph: 'PROVIDER123' },
              { key: 'bh', title: 'Behavioral Health (BH)', desc: 'Ohio BH billing with 837P export', field: 'provider_id', ph: 'BH0000000' },
            ].map(({ key, title, desc, field, ph }) => (
              <Card key={key} className="border-slate-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div><p className="font-medium">{title}</p><p className="text-sm text-slate-500">{desc}</p></div>
                    <Switch checked={formData.contracts[key].enabled} onCheckedChange={(v) => f(`contracts.${key}.enabled`, v)} />
                  </div>
                  {formData.contracts[key].enabled && (
                    <div className="mt-4">
                      <Label>{title} {field === 'contract_number' ? 'Contract Number' : 'Provider ID'}</Label>
                      <Input value={formData.contracts[key][field] || ''} onChange={(e) => f(`contracts.${key}.${field}`, e.target.value)} placeholder={ph} />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Billing */}
          <TabsContent value="billing" className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">Configure how this agency pays AZAI for billing services.</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Payment Method</Label>
                <Select value={formData.payment_config.payment_method} onValueChange={(v) => f('payment_config.payment_method', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}><div className="flex items-center gap-2"><m.icon size={16} />{m.label}</div></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Billing Email</Label>
                <Input type="email" value={formData.payment_config.billing_email} onChange={(e) => f('payment_config.billing_email', e.target.value)} placeholder="billing@agency.com" />
              </div>
              {formData.payment_config.payment_method === 'ach' && (
                <>
                  <div className="col-span-2"><Label>Bank Name</Label><Input value={formData.payment_config.bank_name || ''} onChange={(e) => f('payment_config.bank_name', e.target.value)} placeholder="First National Bank" /></div>
                  <div><Label>Routing Number (last 4)</Label><Input value={formData.payment_config.routing_number || ''} onChange={(e) => f('payment_config.routing_number', e.target.value)} placeholder="****1234" /></div>
                  <div><Label>Account Number (last 4)</Label><Input value={formData.payment_config.account_number || ''} onChange={(e) => f('payment_config.account_number', e.target.value)} placeholder="****5678" /></div>
                </>
              )}
              <div className="col-span-2 flex items-center gap-2">
                <Switch checked={formData.payment_config.auto_pay} onCheckedChange={(v) => f('payment_config.auto_pay', v)} />
                <Label>Enable Auto-Pay</Label>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit}>{isCreate ? 'Create Agency' : 'Save Changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
